import { pickBy, pick } from 'lodash'
import { createSlice } from '@reduxjs/toolkit'
import { fetchExpressList, fetchAreaList, fetchStationList, fetchSupportMethods } from '@/services/api/address'
import { updateAddressVO, setProps as setProviderProps } from '@/store/serviceProviderSlice'

import type { AppDispatch, AppGetState } from '@/store'
import type { ISenderAddressFormValues } from '@/store/senderAddressSlice/types'
import type * as Types from './types'
import { ARRIVAL_METHOD } from '@/constants/arrivalMethod'
import { FormInstance } from 'antd-mobile/es/components/form'
import i18n from '@/i18n'
import { AddressTypeValues, FetchStationListParams } from '@/services/api/address/types'

export const initialState: Types.InitialState = {
  /** 省列表 */
  provinces: [],
  /** 市列表 */
  cities: [],
  /** 区列表 */
  districts: [],
  /** 快递方式 */
  supportedMethods: ['home', 'store'],
  /** 禁止的快递方式 */
  disabledMethods: [],
  /** 服务商列表 */
  expresses: [],
  /** 搜索门店列表关键字 */
  stationQueryKey: undefined,
  /** 门店列表 */
  stationList: [],
  /** 门店列表是否有下一页 */
  stationListHasNext: true,
  /** 门店列表分页当前页数 */
  stationListPageNum: 0,
  /** 请求中 */
  isLoading: false,
  /** 店配选择的门店列表 */
  stationsPickerStations: [],
}

export const recipientAddressSlice = createSlice({
  name: 'recipientAddressForm',
  initialState,
  reducers: {
    update(state, action) {
      const { payload } = action
      return { ...state, ...payload }
    },
    pushStationsPickerStations(state, action) {
      const { stationsPickerStations } = state
      const { payload } = action
      const nextValue = stationsPickerStations.concat(payload)
      return { ...state, stationsPickerStations: nextValue }
    },
  },
})

export const { actions, reducer } = recipientAddressSlice
export default reducer

type AddressFormValues = ISenderAddressFormValues &
  Types.IRecipientAddressFormValues & {
    provider_consignee_name?: string
  }

export interface IInitRecipientAddressFormParams {
  formValues: AddressFormValues
  /** 店配信息 */
  stationRequiredProps?: {
    companyCode: string
    stationCode: string
  }
}

const wrapPromises: Promise<any>[] = []
const wrapLoading = <T extends (...args: any[]) => IPromiseActionThunk<any>>(handle: T): ((...args: Parameters<T>) => IPromiseActionThunk<ReturnType<ReturnType<T>>>) => {
  return (...args: Parameters<T>) =>
    async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
      let promise = null
      const reset = () => {
        const index = wrapPromises.indexOf(promise)
        index !== -1 && wrapPromises.splice(index, 1)
        wrapPromises.length === 0 && dispatch(actions.update({ isLoading: false }))
      }

      try {
        promise = handle(...args)(dispatch, getState, form)
        wrapPromises.push(promise)

        dispatch(actions.update({ isLoading: true }))
        const result = await promise

        reset()
        return result
      } catch (error) {
        reset()
        throw error
      }
    }
}

/** 初始化收货国家 */
export const initRecipientAddressForm = wrapLoading((params: IInitRecipientAddressFormParams) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { formValues, stationRequiredProps } = params
  const departure_countryCode = formValues?.departure_countryCode || form.getFieldValue('departure_countryCode')
  const {
    arrival_countryCode,
    arrival_area,
    arrival_store_companyCode,
    arrival_method,
    arrival_station_type,
    arrival_store_area,
    arrival_zipCode,
    arrival_address,
    arrival_addressTwo,
    arrival_fullName,
    arrival_mobile,
  } = formValues

  if (!arrival_countryCode) {
    return
  }

  /** 店配宅配禁止 */
  const supportedMethods = await fetchSupportMethods({ departureCountryCode: departure_countryCode, arrivalCountryCode: arrival_countryCode })
  if (supportedMethods.length === 0) {
    form.setFields([
      {
        name: 'arrival_method',
        value: undefined,
        errors: [i18n.t('address.form.serviceValid')],
      },
    ])
    return
  }

  const disabledMethods = (['home', 'store'] as AddressTypeValues[]).filter((name) => -1 === supportedMethods.indexOf(name))
  dispatch(actions.update({ disabledMethods }))

  await dispatch(selectRecipientAddressCountry({ arrival_countryCode, arrival_method }))

  arrival_store_companyCode && (await dispatch(selectRecipientAddressStoreCompany()))

  if (departure_countryCode && arrival_method === ARRIVAL_METHOD.store) {
    dispatch(
      fetchRecipientAddressExpress({
        senderAddressName: {
          countryCode: departure_countryCode,
        },
        consigneeAddressName: {
          countryCode: arrival_countryCode,
        },
        needStationType: true,
      })
    )
  }

  if (arrival_store_companyCode && arrival_station_type && stationRequiredProps) {
    dispatch(
      fetchRecipientAddressStoreStation({
        stationCode: stationRequiredProps.stationCode,
        companyCode: stationRequiredProps.companyCode,
      })
    )
  }

  const updateFormValues = pick(
    formValues,
    Object.keys(formValues).filter((key) => key.startsWith('arrival_'))
  )
  form.setFieldsValue(updateFormValues)

  await dispatch(
    setProviderProps({
      arrival_method,
      addressVO: {
        arrival_countryCode,
        arrival_province: arrival_area?.province,
        arrival_city: arrival_area?.city,
        arrival_district: arrival_area?.district,
        arrival_town: arrival_area?.town,
        arrival_zipCode: arrival_zipCode,
        arrival_store_city: arrival_store_area?.city,
        arrival_store_province: arrival_store_area?.province,
        arrival_store_district: arrival_store_area?.district,
        arrival_address,
        arrival_addressTwo,
        arrival_fullName,
        arrival_mobile,
      },
    })
  )
})

/** 获取收货地址省市区 */
export const fetchRecipientAddressArea = wrapLoading((params: Types.FetchRecipientAddressAreaParams) => async (dispatch: AppDispatch) => {
  const provinces = await fetchAreaList(params)
  dispatch(actions.update({ provinces }))
})

/** 获取收货地址可视配置 */
export const getRecipientAddressVisibility = () => (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { address } = getState()
  const countryCode = form.getFieldValue('arrival_countryCode')
  const addressConfig = address?.addressConfig?.[countryCode]

  /* 只有省份隐藏 */
  const isProvinceHidden = !addressConfig.provinceVisibility && addressConfig.cityVisibility && addressConfig.districtVisibility

  /* 只有城市隐藏 */
  const isCityHidden = !addressConfig.cityVisibility && addressConfig.provinceVisibility && addressConfig.districtVisibility

  /* 只有区显示 */
  const isOnlyDistrictShow = addressConfig.districtVisibility && !addressConfig.provinceVisibility && !addressConfig.cityVisibility

  return { isProvinceHidden, isCityHidden, isOnlyDistrictShow }
}

export interface ISelectRecipientAddressCountryParams {
  arrival_countryCode: string
  arrival_method?: number
}

/** 改变收货国家 */
export const selectRecipientAddressCountry = wrapLoading(
  (params: ISelectRecipientAddressCountryParams) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
    const { setFieldsValue, getFieldsValue } = form
    const formValues = getFieldsValue()
    const { arrival_countryCode, arrival_method = formValues.arrival_method } = params

    dispatch(actions.update({ provinces: [] }))

    setFieldsValue({
      arrival_countryCode,
      arrival_address: '',
      arrival_addressTwo: '',
      arrival_zipCode: '',

      arrival_store_companyCode: '',
      arrival_area: { province: '', city: '', district: '' },

      arrival_store_area: { province: '', city: '', district: '' },
      arrival_store_station: null,
    })

    if (arrival_method === ARRIVAL_METHOD.home) {
      // 获取国家列表
      const provinces = await fetchAreaList({ countryCode: arrival_countryCode })
      dispatch(actions.update({ provinces }))
      dispatch(updateAddressVO({ arrival_countryCode: arrival_countryCode }))
    } else {
      form.setFieldsValue({
        arrival_station_type: undefined,
        arrival_store_area: {},
        arrival_store_station: undefined,
      })
    }
  }
)

/** 获取服务产品列表 */
export const fetchRecipientAddressExpress = wrapLoading((params: Types.FetchExpressListParams) => async (dispatch: AppDispatch) => {
  const expresses = await fetchExpressList(params)
  dispatch(actions.update({ expresses }))
})

/** 获取门店列表 | 收货地址 - 店配 */
export const fetchRecipientAddressStoreStation = wrapLoading((params: FetchStationListParams) => async (dispatch: AppDispatch) => {
  const { data: stationList, hasNext: stationListHasNext, pageNum: stationListPageNum } = await fetchStationList(params)
  dispatch(actions.update({ stationList, stationListHasNext, stationListPageNum }))
})

/** 获取门店列表分页 | 收货地址 - 店配 */
export const fetchRecipientAddressStoreStationPagination = wrapLoading((params: FetchStationListParams) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { recipientAddressForm: state } = getState()
  const { data: stationList, hasNext: stationListHasNext, pageNum: stationListPageNum } = await fetchStationList(params)

  if ((params as any).pageNum === 1) {
    dispatch(
      actions.update({
        stationList,
        stationListHasNext,
        stationListPageNum,
      })
    )

    return
  }

  const { stationList: originStationList } = state
  dispatch(
    actions.update({
      stationList: [].concat(originStationList, stationList),
      stationListHasNext,
      stationListPageNum,
    })
  )
})

/** 选中服务商 */
export const selectRecipientAddressStoreCompany = wrapLoading(() => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { setFieldsValue } = form
  dispatch(actions.update({ provinces: [] }))

  setFieldsValue({
    arrival_station_type: undefined,
    arrival_store_area: {},
    arrival_store_station: undefined,
  })
})

/** 选中门店类型 */
export const selectRecipientAddressStoreStationType = () => (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  form.setFieldsValue({
    arrival_store_area: {},
    arrival_store_station: undefined,
  })
}

export interface IQueryRecipientAddressStoreStationKeywordParams {
  queryKey: string
  countryCode?: string
  companyCode?: string
  stationType?: number
  pageNum?: number
}

export interface IQueryRecipientAddressStoreStationAddressParams {
  countryCode?: string
  companyCode?: string
  stationType?: number
  pageNum?: number
  province?: string
  city?: string
  district?: string
}

export interface IPromiseActionThunk<T = void> {
  (dispatch: AppDispatch, getState: AppGetState, form: FormInstance): Promise<T>
}

/** 搜索门店列表 | 收货地址 - 店配 */
interface IQueryRecipientAddressStoreStation {
  (params: IQueryRecipientAddressStoreStationKeywordParams): IPromiseActionThunk
  (params: IQueryRecipientAddressStoreStationAddressParams): IPromiseActionThunk
}

export const queryRecipientAddressStoreStation: IQueryRecipientAddressStoreStation = wrapLoading(
  (params: IQueryRecipientAddressStoreStationKeywordParams | IQueryRecipientAddressStoreStationAddressParams) =>
    async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
      const { getFieldsValue } = form
      const formValues = getFieldsValue()

      const { recipientAddressForm: state } = getState()
      const { stationQueryKey } = state

      const {
        pageNum = 1,
        countryCode = formValues.arrival_countryCode,
        companyCode = formValues.arrival_store_companyCode,
        stationType = formValues.arrival_station_type,
      } = params

      if ('queryKey' in params) {
        const keywords = params.queryKey || stationQueryKey
        dispatch(actions.update({ stationQueryKey: keywords }))
        await dispatch(fetchRecipientAddressStoreStationPagination({ countryCode, companyCode, stationType, keywords, pageNum }))
      } else {
        const optionalPayload = {
          province: params.province || formValues.arrival_store_area?.province,
          city: params.city || formValues.arrival_store_area?.city,
          district: params.district || formValues.arrival_store_area?.district,
        }

        const paylad = pickBy<typeof optionalPayload>(optionalPayload, Boolean)
        await dispatch(fetchRecipientAddressStoreStationPagination({ countryCode, companyCode, stationType, pageNum, ...paylad }))
      }
    }
) as any

/** 搜索门店列表分页 | 收货地址 - 店配 */
export const queryRecipientAddressStoreStationPagination = wrapLoading(() => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { recipientAddressForm: state } = getState()
  const { stationQueryKey, stationListPageNum } = state

  await dispatch(
    queryRecipientAddressStoreStation({
      queryKey: stationQueryKey,
      pageNum: stationListPageNum + 1,
    })
  )
})
